import { Application, RequestHandler, ErrorRequestHandler } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import { Middleware, MiddlewareGroups } from './types';

/**
 * Interface for regular middleware factory functions
 */
export type RegularMiddlewareFactory = {
  create: (config: ConfigManager, logger: Logger, options?: any) =>
    RequestHandler | RequestHandler[];
  dependencies?: string[];
  order?: number;
  isErrorHandler: false;
};

/**
 * Interface for error middleware factory functions
 */
export type ErrorMiddlewareFactory = {
  create: (config: ConfigManager, logger: Logger, options?: any) =>
    ErrorRequestHandler | ErrorRequestHandler[];
  dependencies?: string[];
  order?: number;
  isErrorHandler: true;
};

/**
 * Combined middleware factory type
 */
export type MiddlewareFactory = RegularMiddlewareFactory | ErrorMiddlewareFactory;

/**
 * Registry of available middleware components
 */
const middlewareRegistry: Record<string, MiddlewareFactory> = {};

/**
 * Register a middleware factory function
 */
export function registerMiddleware(name: string, factory: MiddlewareFactory): void {
  middlewareRegistry[name] = factory;
}

/**
 * Get a middleware factory by name
 */
export function getMiddlewareFactory(name: string): MiddlewareFactory | undefined {
  return middlewareRegistry[name];
}

/**
 * Get middleware components sorted by dependencies and order
 */
export function getSortedMiddlewareNames(excludeErrorHandlers: boolean = false): string[] {
  const middleware = Object.entries(middlewareRegistry);

  // Filter out error handlers if requested
  const filteredMiddleware = excludeErrorHandlers
    ? middleware.filter(([_name, factory]) => !factory.isErrorHandler)
    : middleware;

  // Sort by dependencies and order property
  return filteredMiddleware
    .sort(([nameA, factoryA], [nameB, factoryB]) => {
      // If B depends on A, A should come first
      if (factoryB.dependencies?.includes(nameA)) return -1;
      if (factoryA.dependencies?.includes(nameB)) return 1;

      // Otherwise sort by order property (lower goes first)
      return (factoryA.order || 100) - (factoryB.order || 100);
    })
    .map(([name]) => name);
}

/**
 * Expand middleware group names to their individual middleware names
 */
export function expandMiddlewareGroups(middlewareList: (string | Middleware)[]): string[] {
  const result: string[] = [];

  // Process each item in the list
  middlewareList.forEach(middleware => {
    // Check if this is a group name in Object.keys(MiddlewareGroups)
    if (middleware === 'DEFAULT' || middleware === 'API' || middleware === 'MINIMAL') {
      // Get the appropriate group
      let group: Middleware[] = [];

      if (middleware === 'DEFAULT') {
        group = MiddlewareGroups.DEFAULT;
      } else if (middleware === 'API') {
        group = MiddlewareGroups.API;
      } else if (middleware === 'MINIMAL') {
        group = MiddlewareGroups.MINIMAL;
      }

      // Add all middleware from the group
      group.forEach(item => {
        if (!result.includes(item)) {
          result.push(item);
        }
      });
    } else {
      // It's a regular middleware name
      if (!result.includes(middleware.toString())) {
        result.push(middleware.toString());
      }
    }
  });

  return result;
}

/**
 * Check if middleware is enabled based on configuration
 */
export function isMiddlewareEnabled(
  middlewareName: string,
  config: ConfigManager
): boolean {
  // Check specific middleware config first
  const specificEnabled = config.get(`api.${middlewareName}.enabled`);
  if (specificEnabled !== undefined) {
    return Boolean(specificEnabled);
  }

  // Check if middleware is in the global enabled/disabled lists
  const enabledList = config.get<(string | Middleware)[]>('api.middleware.enabled', []);

  // Expand any middleware groups in the enabled list
  const expandedEnabledList = expandMiddlewareGroups(enabledList);
  if (expandedEnabledList.includes(middlewareName)) {
    return true;
  }

  const disabledList = config.get<(string | Middleware)[]>('api.middleware.disabled', []);
  // Expand any middleware groups in the disabled list
  const expandedDisabledList = expandMiddlewareGroups(disabledList);
  if (expandedDisabledList.includes(middlewareName)) {
    return false;
  }

  // Default values based on middleware name
  const defaultsEnabled: Record<string, boolean> = {
    [Middleware.CORS]: true,
    [Middleware.HELMET]: true,
    [Middleware.COMPRESSION]: true,
    [Middleware.BODY_PARSER]: true,
    [Middleware.CORRELATION]: true,
    [Middleware.LOGGING]: true,
    [Middleware.RESPONSE_FORMAT]: true,
    [Middleware.RATE_LIMIT]: false,
    [Middleware.METRICS]: false,
    [Middleware.AUTH]: true,
    [Middleware.ERROR_HANDLING]: true,
    [Middleware.VALIDATION]: false,
    [Middleware.UPLOAD]: false,
    'security': true // Keeping this for backward compatibility
  };

  return defaultsEnabled[middlewareName] ?? false;
}

/**
 * Apply regular middleware from registry
 */
export function applyRegularMiddleware(
  app: Application,
  config: ConfigManager,
  logger: Logger
): void {
  // Get sorted middleware names, excluding error handlers
  const middlewareNames = getSortedMiddlewareNames(true);

  // Apply each middleware based on configuration
  for (const middlewareName of middlewareNames) {
    const factory = getMiddlewareFactory(middlewareName);
    if (!factory || factory.isErrorHandler) continue;

    // Check if the middleware is enabled
    const enabled = isMiddlewareEnabled(middlewareName, config);

    if (enabled) {
      try {
        logger.debug(`Applying middleware: ${middlewareName}`);

        const middleware = (factory as RegularMiddlewareFactory).create(config, logger);

        if (Array.isArray(middleware)) {
          middleware.forEach(m => app.use(m));
        } else {
          app.use(middleware);
        }

        logger.debug(`Successfully applied middleware: ${middlewareName}`);
      } catch (error) {
        logger.error(`Failed to apply middleware: ${middlewareName}`, error as Error);
        // Determine if this is a critical middleware that should fail the startup
        const isRequired = config.get(`api.${middlewareName}.required`, false);
        if (isRequired) {
          throw error;
        }
      }
    } else {
      logger.debug(`Skipping disabled middleware: ${middlewareName}`);
    }
  }
}

/**
 * Apply error handling middleware from registry
 */
export function applyErrorHandlers(
  app: Application,
  config: ConfigManager,
  logger: Logger
): void {
  // Get sorted middleware names, only error handlers
  const errorHandlerNames = Object.entries(middlewareRegistry)
    .filter(([_name, factory]) => factory.isErrorHandler)
    .sort(([_nameA, a], [_nameB, b]) => (a.order || 1000) - (b.order || 1000))
    .map(([name]) => name);

  // Apply each error handler based on configuration
  for (const handlerName of errorHandlerNames) {
    const factory = getMiddlewareFactory(handlerName);
    if (!factory || !factory.isErrorHandler) continue;

    // Check if the middleware is enabled
    const enabled = isMiddlewareEnabled(handlerName, config);

    if (enabled) {
      try {
        logger.debug(`Applying error handler: ${handlerName}`);

        const handler = (factory as ErrorMiddlewareFactory).create(config, logger);

        if (Array.isArray(handler)) {
          handler.forEach(h => app.use(h));
        } else {
          app.use(handler);
        }

        logger.debug(`Successfully applied error handler: ${handlerName}`);
      } catch (error) {
        logger.error(`Failed to apply error handler: ${handlerName}`, error as Error);
        // Determine if this is a critical middleware that should fail the startup
        const isRequired = config.get(`api.${handlerName}.required`, false);
        if (isRequired) {
          throw error;
        }
      }
    } else {
      logger.debug(`Skipping disabled error handler: ${handlerName}`);
    }
  }
}
